import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Notebook, Sparkle, FilePdf, EnvelopeSimple, WhatsappLogo, Phone, Clock, BookOpen, CalendarBlank, Target } from "@phosphor-icons/react";
import AIContent from "@/components/AIContent";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function StudyPlanPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ goal: "", subjects: "Matemática, Redação, Física", hours_per_week: 20, provider: "openai", whatsapp_notification: false, inactivity_rule: "3d" });
  const [loading, setLoading] = useState(false);
  const [waModal, setWaModal] = useState(null); // {student, parent, share_text}
  const [phoneModal, setPhoneModal] = useState(null); // {planId}
  const [phone, setPhone] = useState("");

  async function load() { const r = await api.get("/ai/study-plans"); setPlans(r.data); }
  useEffect(() => { load(); }, []);

  async function generate() {
    setLoading(true);
    try {
      await api.post("/ai/study-plan", {
        student_id: user.id, goal: form.goal,
        subjects: form.subjects.split(",").map(s => s.trim()).filter(Boolean),
        hours_per_week: form.hours_per_week, provider: form.provider,
        whatsapp_notification: form.whatsapp_notification,
        inactivity_rule: form.inactivity_rule,
      });
      toast.success("Plano gerado!"); load();
    } catch (e) { toast.error("Erro ao gerar"); }
    finally { setLoading(false); }
  }

  async function downloadPdf(p) {
    try {
      const r = await api.get(`/ai/study-plans/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `plano-estudos.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch { toast.error("Erro ao gerar PDF"); }
  }

  async function sendEmail(p) {
    try {
      const r = await api.post(`/ai/study-plans/${p.id}/send-email`, { to_student: true, to_parent: true });
      const s = r.data.sent.length;
      const f = r.data.failed.length;
      toast.success(`${s} email(s) enviado(s)${f ? ` • ${f} falhou` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
  }

  async function openWhatsapp(p) {
    try {
      const r = await api.get(`/ai/study-plans/${p.id}/whatsapp-links`);
      const hasAny = r.data.student?.link || r.data.parent?.link;
      if (!hasAny) {
        setPhoneModal({ planId: p.id });
        return;
      }
      setWaModal(r.data);
    } catch (e) { toast.error("Erro ao carregar links"); }
  }

  async function saveMyPhone() {
    if (!phone.trim()) { toast.error("Informe o número"); return; }
    try {
      await api.patch("/users/me/phone", { phone });
      toast.success("Telefone salvo!");
      setPhone("");
      const pid = phoneModal.planId;
      setPhoneModal(null);
      if (pid) {
        const r = await api.get(`/ai/study-plans/${pid}/whatsapp-links`);
        setWaModal(r.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-5xl mx-auto space-y-6">
      <header className="fade-up">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Plano de Estudos IA</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">Monte seu plano personalizado</h1>
      </header>

      <div className="tracing-beam rounded-md p-6 space-y-4 fade-up">
        <div>
          <Label className="text-xs uppercase font-bold tracking-wider">Objetivo</Label>
          <Input data-testid="plan-goal" placeholder="Ex: passar em Medicina na UFRJ"
                 value={form.goal} onChange={e => setForm({...form, goal: e.target.value})} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase font-bold tracking-wider">Matérias (separadas por vírgula)</Label>
          <Input data-testid="plan-subjects" value={form.subjects} onChange={e => setForm({...form, subjects: e.target.value})} className="mt-2"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Horas / semana</Label>
            <Input data-testid="plan-hours" type="number" value={form.hours_per_week}
                   onChange={e => setForm({...form, hours_per_week: parseInt(e.target.value) || 20})} className="mt-2"/>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Motor IA</Label>
            <Select value={form.provider} onValueChange={v => setForm({...form, provider: v})}>
              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">GPT-4o Mini</SelectItem>
                <SelectItem value="gemini">Gemini 2.5 Flash</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="plan-generate" onClick={generate} disabled={loading || !form.goal}
                className="bg-[#002FA7] hover:bg-[#001f7a] brutal-shadow">
          <Sparkle size={16} className="mr-2" weight="fill" /> {loading ? "Gerando plano..." : "Gerar plano com IA"}
        </Button>

        {user?.role !== "student" && (
          <div className="border-t border-zinc-200 pt-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="flex items-center gap-1.5 text-xs uppercase font-bold tracking-wider">
                  <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" />
                  Receber notificações via WhatsApp
                </Label>
                <p className="text-[11px] text-zinc-500 mt-1">Avisa por WhatsApp quando o aluno ficar sem acessar por X dias.</p>
              </div>
              <Switch data-testid="plan-wa-toggle" checked={form.whatsapp_notification}
                      onCheckedChange={(v) => setForm({...form, whatsapp_notification: v})} />
            </div>
            {form.whatsapp_notification && (
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider">Após quanto tempo sem acesso?</Label>
                <Select value={form.inactivity_rule} onValueChange={v => setForm({...form, inactivity_rule: v})}>
                  <SelectTrigger data-testid="plan-wa-rule" className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1d">1 dia sem acesso</SelectItem>
                    <SelectItem value="3d">3 dias sem acesso</SelectItem>
                    <SelectItem value="7d">7 dias sem acesso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-5">
        {plans.map(p => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className="bg-white border border-zinc-200 rounded-3xl overflow-hidden fade-up shadow-sm hover:shadow-md transition-shadow">
            <div className="p-5 sm:p-6 pb-4 bg-gradient-to-br from-[#F4F8FF] via-white to-[#FFF6EA]/40 border-b border-zinc-100">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-md">
                  <Target size={20} color="white" weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E5FFF]">Objetivo</div>
                  <h3 className="font-heading text-lg sm:text-2xl font-black text-[#12143A] leading-tight mt-0.5 break-words">{p.goal}</h3>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1E5FFF] bg-white border border-[#1E5FFF]/20 rounded-full px-3 py-1.5">
                  <Clock size={12} weight="fill" /> {p.hours_per_week}h / semana
                </span>
                {p.subjects?.slice(0, 4).map((s, i) => (
                  <span key={s + i} className="inline-flex items-center gap-1 text-xs font-semibold text-[#12143A] bg-[#FFF6EA] border border-[#FFB86B]/40 rounded-full px-3 py-1.5">
                    <BookOpen size={11} weight="fill" className="text-[#B45309]" /> {s}
                  </span>
                ))}
                {p.subjects?.length > 4 && (
                  <span className="text-xs font-semibold text-zinc-500">+{p.subjects.length - 4}</span>
                )}
                {p.created_at && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 sm:ml-auto">
                    <CalendarBlank size={12} weight="fill" /> {formatDate(p.created_at)}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-center gap-1.5 flex-wrap">
                <Button data-testid={`plan-pdf-${p.id}`} variant="outline" size="sm" onClick={() => downloadPdf(p)}
                        className="h-9 rounded-full border-zinc-200 bg-white/80 backdrop-blur">
                  <FilePdf size={14} weight="fill" className="mr-1 text-[#FF3D71]" /> PDF
                </Button>
                <Button data-testid={`plan-email-${p.id}`} variant="outline" size="sm" onClick={() => sendEmail(p)}
                        className="h-9 rounded-full border-zinc-200 bg-white/80 backdrop-blur">
                  <EnvelopeSimple size={14} weight="fill" className="mr-1 text-[#1E5FFF]" /> Email
                </Button>
                <Button data-testid={`plan-wa-${p.id}`} variant="outline" size="sm" onClick={() => openWhatsapp(p)}
                        className="h-9 rounded-full border-zinc-200 bg-white/80 backdrop-blur">
                  <WhatsappLogo size={14} weight="fill" className="mr-1 text-[#25D366]" /> WhatsApp
                </Button>
              </div>
            </div>
            <div className="p-5 sm:p-6 max-h-[500px] overflow-y-auto">
              <AIContent>{p.plan}</AIContent>
            </div>
          </div>
        ))}
        {plans.length === 0 && (
          <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-3xl">
            Nenhum plano gerado ainda. Preencha o formulário acima e clique em "Gerar plano com IA".
          </div>
        )}
      </div>

      <Dialog open={!!waModal} onOpenChange={(o) => !o && setWaModal(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black flex items-center gap-2">
              <WhatsappLogo size={24} weight="fill" color="#25D366" /> Enviar por WhatsApp
            </DialogTitle>
            <DialogDescription>Clique no link para abrir o WhatsApp já com o plano pré-preenchido.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {waModal?.student?.link ? (
              <a href={waModal.student.link} target="_blank" rel="noreferrer"
                 data-testid="wa-student-link"
                 className="flex items-center gap-3 p-4 rounded-2xl bg-[#E7F9EE] hover:bg-[#D1F3DE] transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsappLogo size={20} color="white" weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#12143A]">Aluno(a)</div>
                  <div className="text-xs text-zinc-500">{waModal.student.name} • {waModal.student.phone}</div>
                </div>
                <div className="text-xs font-bold text-[#25D366]">Abrir →</div>
              </a>
            ) : (
              <div className="p-4 rounded-2xl bg-zinc-50 text-xs text-zinc-500">
                Aluno sem telefone cadastrado.
              </div>
            )}
            {waModal?.parent?.link ? (
              <a href={waModal.parent.link} target="_blank" rel="noreferrer"
                 data-testid="wa-parent-link"
                 className="flex items-center gap-3 p-4 rounded-2xl bg-[#E7F9EE] hover:bg-[#D1F3DE] transition-colors">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsappLogo size={20} color="white" weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[#12143A]">Responsável</div>
                  <div className="text-xs text-zinc-500">{waModal.parent.name} • {waModal.parent.phone}</div>
                </div>
                <div className="text-xs font-bold text-[#25D366]">Abrir →</div>
              </a>
            ) : (
              <div className="p-4 rounded-2xl bg-zinc-50 text-xs text-zinc-500">
                {waModal?.parent === null ? "Nenhum responsável vinculado a este aluno." : "Responsável sem telefone cadastrado."}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!phoneModal} onOpenChange={(o) => !o && setPhoneModal(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black flex items-center gap-2">
              <Phone size={22} weight="duotone" color="#1E5FFF" /> Cadastrar telefone
            </DialogTitle>
            <DialogDescription>
              Para enviar por WhatsApp precisamos do seu número com DDI+DDD. Ex: 5511999998888
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="phone-input" value={phone} onChange={e => setPhone(e.target.value)}
                   placeholder="55 11 99999-8888" className="h-12 rounded-2xl" />
            <Button data-testid="phone-save" onClick={saveMyPhone}
                    className="w-full h-12 rounded-full gradient-primary text-white">
              Salvar telefone e continuar
            </Button>
            <p className="text-[11px] text-zinc-500">
              Se você não tiver telefone do responsável, peça para o(a) administrador ou professor cadastrá-lo pelo painel.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
